<script lang="ts">
  // Admin page (/admin/report-backgrounds): the stock photos every rider can pick as a social
  // report background, for cycling activities, running ones or both. Add seasonal photos here
  // and remove them afterwards; designs saved with a removed photo go back to their default.
  // Riders see them in the order set here (drag a photo, or use its arrows).
  import { onMount } from 'svelte'
  import { Button } from '$lib/components/ui/button'
  import * as Card from '$lib/components/ui/card'
  import * as Dialog from '$lib/components/ui/dialog'
  import { cn } from '@/lib/utils'
  import { addStockReportBackground, getStockReportBackgrounds, orderStockReportBackgrounds, removeStockReportBackground, type StockBackground, type StockBackgroundSport } from '@/Util/api'
  import { ErrorCode } from '@/Util/errorCodes'
  import { notifyApiError, showSuccessToast } from '@/Util/notify'
  import { shrinkImage } from './image'

  const SPORTS: { value: StockBackgroundSport, label: string, icon: string }[] = [
    { value: 'cycling', label: 'Cycling', icon: 'fa-person-biking' },
    { value: 'running', label: 'Running', icon: 'fa-person-running' },
    { value: 'all', label: 'All sports', icon: 'fa-layer-group' },
  ]
  const SPORT_LABELS = Object.fromEntries(SPORTS.map((s) => [s.value, s.label])) as Record<StockBackgroundSport, string>

  let backgrounds: StockBackground[] = []
  let loading = true
  let loadError: string | null = null
  type Filter = 'every' | 'cycling' | 'running'
  const FILTERS: { value: Filter, label: string }[] = [
    { value: 'every', label: 'All' },
    { value: 'cycling', label: 'Cycling' },
    { value: 'running', label: 'Running' },
  ]
  let filter: Filter = 'every'

  let files: File[] = []
  let previews: string[] = []
  let sport: StockBackgroundSport = 'cycling'
  let adding = false
  let fileInput: HTMLInputElement
  let dragging = false

  let dragged: StockBackground | null = null
  let dropTarget: StockBackground | null = null
  let ordering = false

  let removing: StockBackground | null = null
  let removeOpen = false
  let removeBusy = false

  onMount(load)

  async function load() {
    loading = true
    loadError = null
    const res = await getStockReportBackgrounds()
    loading = false
    if (!res.success) {
      loadError = notifyApiError(res, { code: ErrorCode.STOCK_BACKGROUNDS_FAILED, message: 'Could not load the report backgrounds.' })
      return
    }
    backgrounds = res.backgrounds
  }

  // Riders see an "all sports" photo with both cycling and running activities.
  const offeredFor = (bg: StockBackground, f: Filter) => f === 'every' || bg.sport === f || bg.sport === 'all'
  $: shown = backgrounds.filter((bg) => offeredFor(bg, filter))
  $: counts = {
    every: backgrounds.length,
    cycling: backgrounds.filter((bg) => offeredFor(bg, 'cycling')).length,
    running: backgrounds.filter((bg) => offeredFor(bg, 'running')).length,
  }

  function choose(list: FileList | null | undefined) {
    const images = Array.from(list ?? []).filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type))
    if (!images.length) return
    previews.forEach((url) => URL.revokeObjectURL(url))
    files = images
    previews = images.map((f) => URL.createObjectURL(f))
  }

  function clearChosen() {
    previews.forEach((url) => URL.revokeObjectURL(url))
    files = []
    previews = []
    if (fileInput) fileInput.value = ''
  }

  async function add() {
    if (!files.length) return
    adding = true
    let added = 0
    for (const file of files) {
      const res = await addStockReportBackground(await shrinkImage(file), sport)
      if (!res.success) {
        notifyApiError(res, { code: ErrorCode.STOCK_BACKGROUND_SAVE_FAILED, message: `Could not add ${file.name}.` })
        continue
      }
      backgrounds = [res.background, ...backgrounds]
      added++
    }
    adding = false
    if (added) {
      showSuccessToast(`Added ${added} ${added === 1 ? 'background' : 'backgrounds'} for ${SPORT_LABELS[sport].toLowerCase()}.`)
      clearChosen()
    }
  }

  // Move a photo to where `target` is. With a filter on, the photos hidden by it keep their places.
  async function moveTo(bg: StockBackground, target: StockBackground) {
    if (bg.id === target.id || ordering) return
    const previous = backgrounds
    const rest = backgrounds.filter((b) => b.id !== bg.id)
    const at = rest.findIndex((b) => b.id === target.id)
    const later = backgrounds.indexOf(bg) < backgrounds.indexOf(target)
    backgrounds = [...rest.slice(0, later ? at + 1 : at), bg, ...rest.slice(later ? at + 1 : at)]
    ordering = true
    const res = await orderStockReportBackgrounds(backgrounds.map((b) => b.id))
    ordering = false
    if (!res.success) {
      notifyApiError(res, { code: ErrorCode.STOCK_BACKGROUND_SAVE_FAILED, message: 'Could not save the order.' })
      backgrounds = previous
    }
  }

  function moveBy(bg: StockBackground, step: -1 | 1) {
    const target = shown[shown.indexOf(bg) + step]
    if (target) moveTo(bg, target)
  }

  function onDrop() {
    if (dragged && dropTarget) moveTo(dragged, dropTarget)
    dragged = null
    dropTarget = null
  }

  function confirmRemove(bg: StockBackground) {
    removing = bg
    removeOpen = true
  }

  async function remove() {
    if (!removing) return
    removeBusy = true
    const bg = removing
    const res = await removeStockReportBackground(bg.id)
    removeBusy = false
    if (!res.success) {
      notifyApiError(res, { code: ErrorCode.STOCK_BACKGROUND_SAVE_FAILED, message: 'Could not remove that background.' })
      return
    }
    backgrounds = backgrounds.filter((b) => b.id !== bg.id)
    removeOpen = false
    removing = null
  }

  const dateFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
</script>

<div class="mx-auto flex max-w-[1200px] flex-col gap-6 px-4 pb-8">
  <p class="m-0 max-w-[720px] text-sm text-muted-foreground">
    Stock photos every rider can pick as the background of their activity reports, in the Send to Strava dialog and on
    the Activity Reports page. Photos for cycling show with rides, running ones with runs. Changes show to riders
    straight away.
  </p>

  <Card.Root>
    <Card.Header class="pb-3">
      <Card.Title class="text-base">Add backgrounds</Card.Title>
      <Card.Description>JPEG, PNG or WebP, up to 15 MB each. Large photos are scaled to 1600px.</Card.Description>
    </Card.Header>
    <Card.Content class="flex flex-col gap-4">
      <button type="button" on:click={() => fileInput.click()} disabled={adding}
        on:dragover|preventDefault={() => (dragging = true)} on:dragleave={() => (dragging = false)}
        on:drop|preventDefault={(e) => { dragging = false; choose(e.dataTransfer?.files) }}
        class={cn('flex min-h-[132px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-sm transition',
          dragging ? 'border-orange-500 bg-orange-50' : 'border-border bg-muted/30 hover:bg-muted/60')}>
        {#if previews.length}
          <span class="flex flex-wrap justify-center gap-2">
            {#each previews as url}
              <span class="h-20 w-28 rounded-md bg-muted bg-cover bg-center shadow-sm" style="background-image: url('{url}');" />
            {/each}
          </span>
          <span class="text-muted-foreground">{files.length} {files.length === 1 ? 'photo' : 'photos'} chosen · click to choose others</span>
        {:else}
          <i class="fa-solid fa-image text-2xl text-muted-foreground" aria-hidden="true" />
          <span class="font-medium">Drop photos here, or click to choose</span>
          <span class="text-xs text-muted-foreground">You can add several at once</span>
        {/if}
      </button>
      <input bind:this={fileInput} type="file" multiple accept="image/jpeg,image/png,image/webp" class="hidden"
        on:change={(e) => choose(e.currentTarget.files)} />

      <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-muted-foreground" id="bg-sport-label">Show with</span>
          <div class="flex flex-wrap gap-1.5" role="radiogroup" aria-labelledby="bg-sport-label">
            {#each SPORTS as s}
              <button type="button" role="radio" aria-checked={sport === s.value} on:click={() => (sport = s.value)}
                class={cn('inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition',
                  sport === s.value ? 'border-orange-500 bg-orange-500 text-white' : 'border-border bg-background hover:bg-muted')}>
                <i class="fa-solid {s.icon} text-xs" aria-hidden="true" />{s.label}
              </button>
            {/each}
          </div>
        </div>
        <div class="flex gap-2">
          {#if files.length && !adding}
            <Button variant="outline" on:click={clearChosen}>Clear</Button>
          {/if}
          <Button class="bg-orange-500 hover:bg-orange-400 sm:min-w-[170px]" disabled={!files.length || adding} on:click={add}>
            {#if adding}
              <i class="fa-solid fa-spinner-third fa-spin mr-2" aria-hidden="true" />Adding...
            {:else}
              <i class="fa-solid fa-plus mr-2" aria-hidden="true" />Add {files.length > 1 ? `${files.length} backgrounds` : 'background'}
            {/if}
          </Button>
        </div>
      </div>
    </Card.Content>
  </Card.Root>

  <section class="flex flex-col gap-3">
    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 class="m-0 text-base font-semibold">Current backgrounds</h2>
        <p class="m-0 text-xs text-muted-foreground">Riders see them in this order. Drag a photo to move it.</p>
      </div>
      <div class="flex gap-1 rounded-lg bg-muted p-1" role="tablist" aria-label="Filter by sport">
        {#each FILTERS as { value, label }}
          <button type="button" role="tab" aria-selected={filter === value} on:click={() => (filter = value)}
            class={cn('rounded-md px-3 py-1 text-sm font-medium transition',
              filter === value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
            {label} <span class="text-xs text-muted-foreground">{counts[value]}</span>
          </button>
        {/each}
      </div>
    </div>

    {#if loading}
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {#each Array(6) as _}
          <div class="aspect-[3/2] animate-pulse rounded-lg bg-muted" />
        {/each}
      </div>
    {:else if loadError}
      <div class="flex items-center gap-3 text-sm text-red-600">
        {loadError}
        <Button variant="outline" size="sm" on:click={load}>Try again</Button>
      </div>
    {:else if !shown.length}
      <div class="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        {filter === 'every' ? 'No backgrounds yet. Reports will use street view or no photo.' : `No backgrounds show with ${filter} activities.`}
      </div>
    {:else}
      <ul class="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
        {#each shown as bg, i (bg.id)}
          <li draggable={!ordering}
            on:dragstart={(e) => { dragged = bg; if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move' }}
            on:dragover|preventDefault={() => { if (dragged) dropTarget = bg }}
            on:drop|preventDefault={onDrop}
            on:dragend={() => { dragged = null; dropTarget = null }}
            class={cn('group cursor-grab overflow-hidden rounded-lg border bg-background shadow-sm transition active:cursor-grabbing',
              dragged?.id === bg.id && 'opacity-40',
              dropTarget?.id === bg.id && dragged?.id !== bg.id ? 'border-orange-500 ring-2 ring-orange-500' : 'border-border')}>
            <div class="relative aspect-[3/2] bg-muted bg-cover bg-center" style="background-image: url('{bg.url}');">
              <span class="absolute right-2 top-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-black/60 px-2 text-xs font-medium text-white backdrop-blur-sm">{i + 1}</span>
              <span class="absolute left-2 top-2 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
                <i class="fa-solid {SPORTS.find((s) => s.value === bg.sport)?.icon ?? 'fa-layer-group'}" aria-hidden="true" />{SPORT_LABELS[bg.sport] ?? bg.sport}
              </span>
            </div>
            <div class="flex items-center justify-between gap-2 px-3 py-2">
              <span class="truncate text-xs text-muted-foreground">
                {bg.uploaded ? `Added ${bg.created_at ? dateFormat.format(new Date(bg.created_at)) : ''}` : 'Original photo'}
              </span>
              <div class="flex flex-none items-center gap-0.5">
                <Button variant="ghost" size="sm" class="h-8 w-8 p-0" title="Move earlier" aria-label="Move earlier" disabled={i === 0 || ordering} on:click={() => moveBy(bg, -1)}>
                  <i class="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                </Button>
                <Button variant="ghost" size="sm" class="h-8 w-8 p-0" title="Move later" aria-label="Move later" disabled={i === shown.length - 1 || ordering} on:click={() => moveBy(bg, 1)}>
                  <i class="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Button>
                <Button variant="ghost" size="sm" class="text-red-600 hover:bg-red-50 hover:text-red-700" on:click={() => confirmRemove(bg)}>
                  <i class="fa-solid fa-trash-can mr-1.5 text-xs" aria-hidden="true" />Remove
                </Button>
              </div>
            </div>
          </li>
        {/each}
      </ul>
    {/if}
  </section>
</div>

<Dialog.Root portal="#shadcn-ui-dialog-target" bind:open={removeOpen}>
  <Dialog.Content class="max-w-md">
    <Dialog.Header>
      <Dialog.Title>Remove this background?</Dialog.Title>
      <Dialog.Description>
        Riders can no longer pick it. Saved designs that use it go back to their default background. Reports already
        sent keep it.
      </Dialog.Description>
    </Dialog.Header>
    {#if removing}
      <div class="aspect-[3/2] w-full rounded-md bg-muted bg-cover bg-center" style="background-image: url('{removing.url}');" />
    {/if}
    <Dialog.Footer class="gap-2">
      <Button variant="outline" on:click={() => (removeOpen = false)}>Cancel</Button>
      <Button variant="destructive" disabled={removeBusy} on:click={remove}>
        {#if removeBusy}<i class="fa-solid fa-spinner-third fa-spin mr-2" aria-hidden="true" />{/if}Remove
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
